import React, { useState } from 'react';
import SiteLayout from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, Clock, Users, HelpCircle, Gift, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCMSContent } from '../hooks/useCMSContent';

// Icon mapping for dynamic icon rendering from CMS
const iconMap = {
  Clock, Users, HelpCircle, Gift
};

const DemoPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const { content: cmsContent, loading } = useCMSContent('demo-page');

  // Use CMS content only - no fallbacks
  const hero = cmsContent?.hero;
  const benefits = cmsContent?.benefits;
  const benefitsTitle = cmsContent?.benefitsTitle;
  const form = cmsContent?.form;
  const roleOptions = cmsContent?.roleOptions;
  const auditorOptions = cmsContent?.auditorOptions;

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormLoading(true);

    setTimeout(() => {
      setFormLoading(false);
      setSubmitted(true);
      if (form?.successToast) {
        toast.success(form.successToast);
      }
    }, 1000);
  };

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Show error if CMS content is not configured
  if (!hero || !form) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Demo page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/demo-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero, form
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {hero && (
        <section className="py-20 bg-muted/30">
          <div className="container-wide text-center">
            {hero.title && (
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {hero.title}
              </h1>
            )}
            {hero.subtitle && (
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {hero.subtitle}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="py-20">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Benefits Column */}
            {benefits && benefits.length > 0 && (
              <div>
                {benefitsTitle && <h2 className="text-2xl font-bold mb-8">{benefitsTitle}</h2>}
                <div className="space-y-6">
                  {benefits.map((benefit, index) => {
                    const Icon = iconMap[benefit.icon] || Clock;
                    return (
                      <div key={index} className="flex gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">{benefit.title}</h3>
                          <p className="text-muted-foreground text-sm">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Form Column */}
            <div>
              {submitted ? (
                <div className="text-center py-12 bg-card border border-border rounded-xl shadow-sm">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  {form.successTitle && (
                    <h2 className="text-2xl font-bold mb-4">{form.successTitle}</h2>
                  )}
                  {form.successMessage && (
                    <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                      {form.successMessage}
                    </p>
                  )}
                  {form.submitAnotherButton && (
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      {form.submitAnotherButton}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
                  {form.title && <h2 className="text-xl font-bold mb-6">{form.title}</h2>}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">{form.firstNameLabel}</Label>
                        <Input id="firstName" placeholder={form.firstNamePlaceholder} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">{form.lastNameLabel}</Label>
                        <Input id="lastName" placeholder={form.lastNamePlaceholder} required />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{form.emailLabel}</Label>
                      <Input id="email" type="email" placeholder={form.emailPlaceholder} required />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">{form.companyLabel}</Label>
                      <Input id="company" placeholder={form.companyPlaceholder} required />
                    </div>

                    {(roleOptions || auditorOptions) && (
                      <div className="grid md:grid-cols-2 gap-6">
                        {roleOptions && roleOptions.length > 0 && (
                          <div className="space-y-2">
                            <Label htmlFor="role">{form.roleLabel}</Label>
                            <select
                              id="role"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <option value="">{form.rolePlaceholder}</option>
                              {roleOptions.map((opt, idx) => (
                                <option key={idx} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {auditorOptions && auditorOptions.length > 0 && (
                          <div className="space-y-2">
                            <Label htmlFor="auditors">{form.auditorsLabel}</Label>
                            <select
                              id="auditors"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <option value="">{form.auditorsPlaceholder}</option>
                              {auditorOptions.map((opt, idx) => (
                                <option key={idx} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="message">{form.messageLabel}</Label>
                      <Textarea
                        id="message"
                        placeholder={form.messagePlaceholder}
                        className="min-h-[120px]"
                      />
                    </div>

                    <Button type="submit" size="lg" className="w-full" disabled={formLoading}>
                      {formLoading ? form.sendingText : form.submitButton}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default DemoPage;
